<template>
  <modal-wrap :name="modalName" :hide-footer="true" size="lg">
    <template #modal-title>Import Addresses</template>
    <template #default>
      <div v-if="!result" class="mt-2">
        <div class="mb-3">
          <label class="form-label required">File</label>
          <div class="input-group">
            <div
              class="select-file"
              :class="{ 'select-file--drag-over': isDragOver }"
              @dragover.prevent="isDragOver = true"
              @dragleave.prevent="isDragOver = false"
              @drop.prevent="onDrop"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".csv,.txt,.xlsx"
                @change="onFileChange"
              />
              <div v-if="selectedFile">
                <span>{{ selectedFile.name }}</span>
              </div>
              <div v-else><span>Choose file</span> or drop file here</div>
            </div>
          </div>
        </div>
        <div class="mb-3">
          <label>File requirements</label>
          <div class="file-requirements">
            <div class="mb-1">Format: <strong>CSV, TXT, XLSX</strong></div>
            <div class="d-flex gap-2">
              <a
                href="https://cdn-pacsortonline.s3.eu-north-1.amazonaws.com/frontend/sample-address-book.csv"
                class="download-sample"
                download="sample-address-book.csv"
              >
                Download CSV example
              </a>
              <a
                href="https://cdn-pacsortonline.s3.eu-north-1.amazonaws.com/frontend/sample-address-book.txt"
                class="download-sample"
                download="sample-address-book.txt"
              >
                Download TXT example
              </a>
              <a
                href="https://cdn-pacsortonline.s3.eu-north-1.amazonaws.com/frontend/sample-address-book.xlsx"
                class="download-sample"
                download="sample-address-book.xlsx"
              >
                Download XLSX example
              </a>
            </div>
          </div>
        </div>
        <div v-if="uploadError" class="text-danger mb-2">
          <b>Error: </b>{{ uploadError }}
        </div>
        <div class="d-flex justify-content-end mt-3">
          <b-button variant="success" :disabled="!selectedFile || uploading" @click="handleUpload">
            <b-spinner v-if="uploading" small class="me-1" />
            {{ uploading ? 'Uploading...' : 'Upload' }}
          </b-button>
        </div>
      </div>

      <div v-else class="mt-2">
        <div class="d-flex gap-3 mb-3">
          <div class="text-center px-3">
            <div class="fs-4 fw-bold text-success">{{ result.created }}</div>
            <div class="text-muted small">Created</div>
          </div>
          <div class="text-center px-3">
            <div class="fs-4 fw-bold text-warning">{{ result.duplicates }}</div>
            <div class="text-muted small">Duplicates</div>
          </div>
          <div class="text-center px-3">
            <div class="fs-4 fw-bold text-danger">{{ result.failed }}</div>
            <div class="text-muted small">Failed</div>
          </div>
        </div>

        <div v-if="result.errors.length > 0" class="mb-3">
          <h6>Row Errors</h6>
          <div class="table-responsive" style="max-height: 240px; overflow-y: auto">
            <table class="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th style="width: 80px">Row</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="err in result.errors" :key="err.row">
                  <td>{{ err.row }}</td>
                  <td>{{ err.message }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-3">
          <b-button variant="secondary" @click="reset">Import Another</b-button>
          <b-button variant="success" @click="closeModal">Done</b-button>
        </div>
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { ImportResult } from '@/types/Models/AddressBookImport'
import { BButton, BSpinner } from 'bootstrap-vue-next'
import { defineComponent, ref, computed, watch, type Ref } from 'vue'
import { ADDRESS_BOOK_IMPORT } from '@/const/ModalTypes'
import { useAddressBookStore } from '@/store/addressBook'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import AddressBookProxy from '@/proxies/AddressBookProxy'

export default defineComponent({
  name: 'AddressBookImportModal',
  components: { BButton, BSpinner, ModalWrap },
  setup() {
    const modalStore = useVueNextModalStore()
    const addressBookStore = useAddressBookStore()

    const modalName = ADDRESS_BOOK_IMPORT
    const fileInput: Ref<HTMLInputElement | null> = ref(null)
    const selectedFile: Ref<File | null> = ref(null)
    const uploading: Ref<boolean> = ref(false)
    const result: Ref<ImportResult | null> = ref(null)
    const uploadError: Ref<string | null> = ref(null)
    const isDragOver: Ref<boolean> = ref(false)

    const modalData = computed(() => modalStore.modalPropsByName(modalName))
    const companyId = computed<string | null>(() => modalData.value?.companyId ?? null)

    const onFileChange = (event: Event) => {
      const input = event.target as HTMLInputElement
      selectedFile.value = input.files?.[0] ?? null
    }

    const onDrop = (event: DragEvent) => {
      isDragOver.value = false
      selectedFile.value = event.dataTransfer?.files?.[0] ?? null
    }

    const handleUpload = () => {
      if (!selectedFile.value || uploading.value) return
      uploading.value = true
      uploadError.value = null

      const proxy = new AddressBookProxy()
      proxy
        .import(selectedFile.value, companyId.value)
        .then((data: ImportResult) => {
          result.value = data
          const params = companyId.value ? { company_id: companyId.value } : {}
          if (data.created > 0) {
            addressBookStore.fetchAll(params)
          }
        })
        .catch((error: any) => {
          uploadError.value =
            error?.message ?? error?.errors?.[Object.keys(error.errors ?? {})[0]]?.[0] ?? 'Upload failed.'
        })
        .finally(() => {
          uploading.value = false
        })
    }

    const reset = () => {
      result.value = null
      uploadError.value = null
      selectedFile.value = null
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const closeModal = () => {
      modalStore.closeModal(modalName)
    }

    const isOpen = computed(() => modalStore.isModalVisible(modalName))
    watch(isOpen, (open) => {
      if (!open) reset()
    })

    return {
      modalName,
      fileInput,
      selectedFile,
      uploading,
      result,
      uploadError,
      isDragOver,
      onFileChange,
      onDrop,
      handleUpload,
      reset,
      closeModal,
    }
  },
})
</script>

<style lang="scss" scoped>
.select-file {
  height: 140px;

  &--drag-over {
    border-color: variables.$green;
    background: rgba(variables.$green, 0.05);
  }
}

.file-requirements {
  height: auto;
}
</style>
